import { useMemo } from 'react';

import { type QuizGenerateItem } from '@/api/ai/type';
import type { WebsiteData } from '@/api/ai/website/types';
import type { YoutubeVideoData } from '@/api/ai/youtube/types';
import {
  type AchievementStandardWithOrigin,
  type BuildPromptOptions,
} from '@/shared/features/ai-quiz-generation/types';

import { type AttachmentState } from './types';
import { useAiByFile } from './useAiByFile';
import { useAiByQuery } from './useAiByQuery';
import { useAiByWebsite } from './useAiByWebsite';
import { useAiByYoutube } from './useAiByYoutube';

/**
 * 퀴즈 생성 파라미터
 */
interface GenerateParams {
  attachmentState: AttachmentState;
  textPrompt: string;
  topicId?: string;
  options: BuildPromptOptions;
  selectedStandards: AchievementStandardWithOrigin[];
  /**
   * 유저 모드 선택. omit (undefined) 시 서버 기본 모델 결정 경로.
   * - 'fast'     → in-process Gemini
   * - 'thorough' → Bedrock Claude Opus
   */
  generationMode?: 'fast' | 'thorough';
  /**
   * Stage 1 UI cohort. 클라이언트가 Flipt 으로 결정해 wire 로 그대로 전달한다.
   * 서버는 'dropdown' 일 때만 Stage 2 backend_cohort (real/placebo) 를 평가하고,
   * 그 외엔 backend_cohort = "n/a" 로 단락한다 (placebo blind 강제).
   */
  uiCohort?: 'as_is' | 'dropdown';
}

/**
 * useAiGenerationStrategy 파라미터
 */
interface UseAiGenerationStrategyParams {
  /** 퀴즈 생성 성공 시 호출 */
  onGenerateSuccess: (items: QuizGenerateItem[]) => void;
  /** 퀴즈 생성 시작 시 호출 */
  onGenerateStart: (isRegenerate: boolean) => void;
  /** 퀴즈 생성 에러 시 호출 */
  onGenerateError: () => void;
  /** Attachment 상태 업데이트 */
  onAttachmentUpdate: (state: AttachmentState) => void;
  /** 생성 완료 시 호출 (성공 후 추가 처리) */
  onSuccess: () => void;
  /** 현재 생성 시도 횟수 */
  tryCount: number;
  /** 세션 추적용 UUID */
  generationId: string;
}

/**
 * AI 퀴즈 생성 전략 관리 훅
 *
 * useAiBuild의 생성 로직 라우팅 책임을 분리한 훅입니다.
 * switch 문 대신 전략 패턴을 사용하여 각 attachment 타입별로
 * 일관된 인터페이스를 제공합니다.
 *
 * 주요 기능:
 * - 공통 콜백을 한 번만 생성하여 중복 제거
 * - file/youtube/query 훅을 조율하여 전략 맵 생성
 * - attachment 타입에 따라 적절한 전략 자동 선택
 * - 타입 안전한 생성 로직 제공 (타입 단언 제거)
 *
 * @param params - 콜백 및 상태 업데이트 함수
 * @returns attachment 타입별 전략 맵
 *
 * @example
 * ```typescript
 * const strategies = useAiGenerationStrategy({
 *   onGenerateSuccess,
 *   onGenerateStart,
 *   onGenerateError,
 *   onAttachmentUpdate,
 *   onSuccess,
 *   tryCount,
 * });
 *
 * // File 첨부
 * await strategies.file.handleAttach(file);
 *
 * // 현재 타입에 맞는 전략 사용
 * const currentStrategy = strategies[attachmentState.type];
 * currentStrategy.handleGenerate({ attachmentState, textPrompt, options, selectedStandards });
 * ```
 */

export const useAiGenerationStrategy = (
  params: UseAiGenerationStrategyParams,
) => {
  const {
    onGenerateSuccess,
    onGenerateStart,
    onGenerateError,
    onAttachmentUpdate,
    onSuccess,
    tryCount,
    generationId,
  } = params;

  /**
   * 공통 콜백 객체
   * 3개 훅에서 중복되던 콜백 래핑을 한 번만 생성
   */
  const sharedCallbacks = useMemo(
    () => ({
      onGenerateStart,
      onGenerateSuccess: (items: QuizGenerateItem[]) => {
        onGenerateSuccess(items);
        onSuccess();
      },
      onGenerateError,
      tryCount,
      generationId,
    }),
    [
      onGenerateStart,
      onGenerateSuccess,
      onGenerateError,
      onSuccess,
      tryCount,
      generationId,
    ],
  );

  /**
   * File 방식 훅
   */
  const fileHook = useAiByFile({
    ...sharedCallbacks,
    onAttach: attachment => {
      if (attachment instanceof File) {
        onAttachmentUpdate({ type: 'file', data: attachment });
      }
    },
    onDetach: () => {
      onAttachmentUpdate({ type: 'none', data: null });
    },
  });

  /**
   * YouTube 방식 훅
   */
  const youtubeHook = useAiByYoutube({
    ...sharedCallbacks,
    onAttach: attachment => {
      if (attachment) {
        onAttachmentUpdate({
          type: 'youtube',
          data: attachment as YoutubeVideoData,
        });
      }
    },
    onDetach: () => {
      onAttachmentUpdate({ type: 'none', data: null });
    },
  });

  /**
   * Website 방식 훅
   */
  const websiteHook = useAiByWebsite({
    ...sharedCallbacks,
    onAttach: attachment => {
      if (attachment) {
        onAttachmentUpdate({
          type: 'website',
          data: attachment as WebsiteData,
        });
      }
    },
    onDetach: () => {
      onAttachmentUpdate({ type: 'none', data: null });
    },
  });

  /**
   * Query 방식 훅
   */
  const queryHook = useAiByQuery({
    ...sharedCallbacks,
    onAttach: () => {
      // Query 방식은 첨부물 없음
    },
    onDetach: () => {
      // Query 방식은 첨부물 없음
    },
  });

  /**
   * 전략 맵 반환
   * 각 attachment 타입별로 적절한 인터페이스 제공
   */
  return useMemo(
    () => ({
      /**
       * File 전략
       */
      file: {
        handleAttach: fileHook.handleAttach,
        handleDetach: fileHook.handleDetach,
        handleGenerate: ({
          attachmentState,
          textPrompt,
          options,
          selectedStandards,
          generationMode,
          uiCohort,
        }: GenerateParams) => {
          const { type, data } = attachmentState;
          if (type === 'file' && data) {
            // TypeScript discriminated union narrowing으로 data는 File 타입
            void fileHook.handleGenerate(
              data,
              options,
              selectedStandards,
              textPrompt,
              generationMode,
              uiCohort,
            );
          }
        },
      },

      /**
       * YouTube 전략
       */
      youtube: {
        /**
         * YouTube 첨부 핸들러
         * 현재 attachmentState를 받아서 기존 영상 데이터가 있으면 모달에 전달
         */
        handleAttach: async (currentAttachmentState: AttachmentState) => {
          const existingData =
            currentAttachmentState.type === 'youtube'
              ? currentAttachmentState.data
              : undefined;
          await youtubeHook.handleAttach(existingData);
        },
        handleDetach: youtubeHook.handleDetach,
        handleGenerate: ({
          attachmentState,
          textPrompt,
          options,
          selectedStandards,
          generationMode,
          uiCohort,
        }: GenerateParams) => {
          const { type, data } = attachmentState;
          if (type === 'youtube' && data) {
            // TypeScript discriminated union narrowing으로 data는 YoutubeVideoData 타입
            void youtubeHook.handleGenerate(
              data,
              options,
              selectedStandards,
              textPrompt,
              generationMode,
              uiCohort,
            );
          }
        },
      },

      /**
       * Website 전략
       */
      website: {
        /**
         * Website 첨부 핸들러
         * 현재 attachmentState를 받아서 기존 website 데이터가 있으면 모달에 전달
         */
        handleAttach: async (currentAttachmentState: AttachmentState) => {
          const existingData =
            currentAttachmentState.type === 'website'
              ? currentAttachmentState.data
              : undefined;
          await websiteHook.handleAttach(existingData);
        },
        handleDetach: websiteHook.handleDetach,
        handleGenerate: ({
          attachmentState,
          textPrompt,
          options,
          selectedStandards,
          generationMode,
          uiCohort,
        }: GenerateParams) => {
          const { type, data } = attachmentState;
          if (type === 'website' && data) {
            // TypeScript discriminated union narrowing으로 data는 WebsiteData 타입
            void websiteHook.handleGenerate(
              data,
              options,
              selectedStandards,
              textPrompt,
              generationMode,
              uiCohort,
            );
          }
        },
      },

      /**
       * Query(prompt only) 전략
       */
      none: {
        handleAttach: queryHook.handleAttach,
        handleDetach: queryHook.handleDetach,
        handleGenerate: ({
          textPrompt,
          topicId,
          options,
          selectedStandards,
          generationMode,
          uiCohort,
        }: GenerateParams) => {
          void queryHook.handleGenerate(
            textPrompt,
            options,
            selectedStandards,
            topicId,
            generationMode,
            uiCohort,
          );
        },
      },
    }),
    [fileHook, youtubeHook, websiteHook, queryHook],
  );
};
